import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { DirectorBureauProps, DirectorBureauRow, ReportData } from '../tokens/types';
import { PdfSection } from '../primitives/PdfPrimitives';
import { ScreenSection } from '../primitives/ScreenPrimitives';
import { colors, fontFamily, typeScale } from '../tokens/designTokens';

// ─── Layout constants (pt) ───────────────────────────────────────────────
// Match the Figma 171:4058 column widths. Total = 672.75pt — wider than the
// PDF body (~526pt) by design; the screen container scrolls/scales while the
// PDF uses flex:1 columns that compress proportionally.
const COL_BUREAU = 160;
const COL_SCORE = 170;
const COL_DPD = 90;
const COL_OVERDUE = 90;
const COL_MAXDPD = 90;
const COL_FETCHED = 72.75;
const TABLE_WIDTH = COL_BUREAU + COL_SCORE + COL_DPD + COL_OVERDUE + COL_MAXDPD + COL_FETCHED; // 672.75

const HEADER_BG = '#f9fafc';
const GROUP_BG = '#f2f2f2';
const BORDER = '#e7e7e7';

// Row heights compacted (was 38/28/32) so 1-2 extra directors fit per page
// before paginating — recovers the trailing-whitespace gap when a final
// short bureau row got pushed wholesale to the next page.
const ROW_H = 32;
const HEADER_H = 26;
const GROUP_H = 28;

// ─── Tone helpers (badge colours adapt to severity) ──────────────────────
// 'muted' is the no-data tone — distinct from 'pos'. Earlier the tone fns
// returned 'pos' for null/undefined, which mis-rendered "bureau not pulled"
// as a healthy green 0. Callers must branch on null themselves and only
// invoke the severity fns with defined values.
type Tone = 'pos' | 'warn' | 'neg' | 'muted';
const toneInk = (t: Tone) => (t === 'pos' ? colors.pos : t === 'warn' ? colors.warn : t === 'neg' ? colors.neg : colors.ink40);
const toneBg = (t: Tone) =>
    t === 'pos' ? colors.posSoft : t === 'warn' ? colors.warnSoft : t === 'neg' ? colors.negSoft : colors.surfaceAlt;

const dpdAccountsTone = (n: number): Tone => {
    if (n <= 0) return 'pos';
    if (n <= 2) return 'warn';
    return 'neg';
};
const overdueTone = (n: number): Tone => {
    if (n <= 0) return 'pos';
    if (n < 10000) return 'warn';
    return 'neg';
};
const maxDpdTone = (n: number): Tone => {
    if (n <= 0) return 'pos';
    if (n <= 30) return 'warn';
    return 'neg';
};

// Inter (default body/mono) covers ₹ correctly, but sanitize anyway so a
// future swap to GeistMono on the value column won't surface a tofu (Skill
// rule 10).
const sanitizeForMono = (s: string): string =>
    s
        .replace(/₹\s*/g, 'Rs. ')
        .replace(/\s{2,}/g, ' ')
        .trim();

const fmtScore = (s?: number) => (s == null ? '—' : `${Math.round(s)} /900`);
const fmtRupees = (n?: number) => (n == null ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`);
const fmtDays = (n?: number) => (n == null ? '—' : `${n} days`);
const fmtFetched = (n?: number) => {
    if (n == null) return '—';
    if (n === 0) return 'Today';
    if (n === 1) return '1 day ago';
    return `${n} days ago`;
};
const scoreFraction = (s?: number) => {
    if (s == null) return 0;
    return Math.max(0, Math.min(1, s / 900));
};

// ─── PDF ─────────────────────────────────────────────────────────────────

const PdfBadge: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
    <View
        style={{
            backgroundColor: toneBg(tone),
            borderRadius: 999,
            paddingHorizontal: 8,
            paddingTop: 2,
            paddingBottom: 3,
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'center',
            flexShrink: 0,
        }}
    >
        <Text
            style={{
                fontSize: 8,
                fontFamily: fontFamily.bodyBold,
                color: toneInk(tone),
                lineHeight: 1,
            }}
        >
            {children}
        </Text>
    </View>
);

const PdfScoreBar: React.FC<{ score?: number }> = ({ score }) => {
    const frac = scoreFraction(score);
    return (
        <View style={{ flex: 1, height: 5, backgroundColor: '#EDE8F5', borderRadius: 2.5, overflow: 'hidden' }}>
            {/* react-pdf can't render gradients; approximate with a flat purple in the brand range. */}
            {frac > 0 ? (
                <View
                    style={{
                        width: `${frac * 100}%`,
                        height: 5,
                        backgroundColor: colors.accent,
                        borderRadius: 2.5,
                    }}
                />
            ) : null}
        </View>
    );
};

const PdfHeaderRow: React.FC = () => (
    <View
        style={{
            flexDirection: 'row',
            backgroundColor: HEADER_BG,
            height: HEADER_H,
            alignItems: 'center',
            borderBottomWidth: 0.5,
            borderBottomColor: BORDER,
        }}
        wrap={false}
    >
        <Text style={[styles.headerCell, { width: COL_BUREAU, paddingLeft: 12 }]}>Bureau</Text>
        <Text style={[styles.headerCell, { width: COL_SCORE }]}>Score</Text>
        <Text style={[styles.headerCell, { width: COL_DPD, textAlign: 'center' }]}>DPD Accounts</Text>
        <Text style={[styles.headerCell, { width: COL_OVERDUE, textAlign: 'center' }]}>Overdue</Text>
        <Text style={[styles.headerCell, { width: COL_MAXDPD, textAlign: 'center' }]}>Max DPD</Text>
        <Text style={[styles.headerCell, { width: COL_FETCHED, textAlign: 'right', paddingRight: 12 }]}>Fetched</Text>
    </View>
);

const PdfGroupRow: React.FC<{ name: string }> = ({ name }) => (
    // minPresenceAhead = group header + 1 row exactly (no extra buffer) so
    // a single-row director group can land at the very bottom of a page if
    // there's just enough space, instead of being pushed wholesale.
    <View
        minPresenceAhead={GROUP_H + ROW_H}
        style={{
            flexDirection: 'row',
            backgroundColor: GROUP_BG,
            height: GROUP_H,
            alignItems: 'center',
            paddingLeft: 12,
            borderBottomWidth: 0.5,
            borderBottomColor: BORDER,
        }}
        wrap={false}
    >
        <Text style={{ fontSize: typeScale.body, fontFamily: fontFamily.bodyBold, color: colors.ink00 }}>{name}</Text>
    </View>
);

const PdfBureauRow: React.FC<{ row: DirectorBureauRow; isLast: boolean }> = ({ row, isLast }) => (
    <View
        style={{
            flexDirection: 'row',
            height: ROW_H,
            alignItems: 'center',
            borderBottomWidth: isLast ? 0 : 0.5,
            borderBottomColor: BORDER,
        }}
        wrap={false}
    >
        <View style={{ width: COL_BUREAU, paddingLeft: 36 }}>
            <Text style={{ fontSize: typeScale.body, color: colors.ink40 }}>{row.bureau}</Text>
        </View>
        <View
            style={{
                width: COL_SCORE,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingRight: 12,
            }}
        >
            {/* skill rule 9 corollary: text + score bar in same row — lineHeight:1 keeps glyph centered against bar */}
            <Text
                style={{ fontSize: typeScale.body, color: colors.ink00, fontFamily: fontFamily.bodyMedium, flexShrink: 0, lineHeight: 1 }}
            >
                {sanitizeForMono(fmtScore(row.score))}
            </Text>
            <PdfScoreBar score={row.score} />
        </View>
        <View style={{ width: COL_DPD, alignItems: 'center' }}>
            <PdfBadge tone={row.dpdAccounts == null ? 'muted' : dpdAccountsTone(row.dpdAccounts)}>
                {row.dpdAccounts == null ? '—' : row.dpdAccounts}
            </PdfBadge>
        </View>
        <View style={{ width: COL_OVERDUE, alignItems: 'center' }}>
            <PdfBadge tone={row.currentOverdue == null ? 'muted' : overdueTone(row.currentOverdue)}>
                {row.currentOverdue == null ? '—' : fmtRupees(row.currentOverdue)}
            </PdfBadge>
        </View>
        <View style={{ width: COL_MAXDPD, alignItems: 'center' }}>
            <PdfBadge tone={row.maxDpds == null ? 'muted' : maxDpdTone(row.maxDpds)}>
                {row.maxDpds == null ? '—' : fmtDays(row.maxDpds)}
            </PdfBadge>
        </View>
        <View style={{ width: COL_FETCHED, paddingRight: 12 }}>
            <Text style={{ fontSize: typeScale.caption, color: colors.ink40, textAlign: 'right' }}>{fmtFetched(row.fetchedDaysAgo)}</Text>
        </View>
    </View>
);

const styles = {
    headerCell: {
        fontSize: typeScale.tableHead,
        fontFamily: fontFamily.bodyBold,
        color: colors.ink20,
        letterSpacing: 0.3,
    },
} as const;

const PdfDirectorBureau: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const d = data.directorBureau;
    if (!d || d.directors.length === 0) return null;
    return (
        <PdfSection
            kicker="Section · Management"
            title="Director Bureau"
            subtitle="Bureau scores and credit health for all directors · Source: Experian, Equifax, CIBIL"
            index={index}
            breakBefore={breakBefore}
        >
            <View
                style={{
                    borderWidth: 0.5,
                    borderColor: BORDER,
                    borderRadius: 4,
                    overflow: 'hidden',
                    marginTop: 8,
                }}
            >
                <PdfHeaderRow />
                {d.directors.map((dir, di) => (
                    // No wrap=false on the per-director container — each row inside
                    // is already wrap=false. Letting the group flow means a director's
                    // header + rows can split across pages cleanly, and a trailing
                    // single-row group can fill leftover space at the bottom of the
                    // previous page instead of being pushed wholesale to a new page.
                    // PdfGroupRow's minPresenceAhead glues the header to at least the
                    // first row so the header alone never orphans.
                    <React.Fragment key={di}>
                        <PdfGroupRow name={dir.name} />
                        {dir.rows.map((r, ri) => (
                            <PdfBureauRow key={ri} row={r} isLast={di === d.directors.length - 1 && ri === dir.rows.length - 1} />
                        ))}
                    </React.Fragment>
                ))}
            </View>
        </PdfSection>
    );
};

// ─── Screen ──────────────────────────────────────────────────────────────

const SCORE_GRADIENT = 'linear-gradient(to right, #e6d9ff, #aa7eff, rgba(96,33,179,0.75))';

const ScreenBadge: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
    <span
        style={{
            background: toneBg(tone),
            color: toneInk(tone),
            fontWeight: 700,
            fontSize: 11,
            lineHeight: 1,
            padding: '4px 10px',
            borderRadius: 999,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        {children}
    </span>
);

const ScreenScoreBar: React.FC<{ score?: number }> = ({ score }) => {
    const frac = scoreFraction(score);
    return (
        <div
            style={{
                flex: 1,
                height: 6,
                background: '#EDE8F5',
                borderRadius: 3,
                overflow: 'hidden',
                minWidth: 60,
            }}
        >
            {frac > 0 ? (
                <div
                    style={{
                        width: `${frac * 100}%`,
                        height: '100%',
                        background: SCORE_GRADIENT,
                        borderRadius: 3,
                    }}
                />
            ) : null}
        </div>
    );
};

const ScreenDirectorBureau: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const d = data.directorBureau;
    if (!d || d.directors.length === 0) return null;
    return (
        <ScreenSection
            kicker="Section · Management"
            title="Director Bureau"
            subtitle="Bureau scores and credit health for all directors · Source: Experian, Equifax, CIBIL"
            index={index}
        >
            <div
                style={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: 6,
                    overflow: 'hidden',
                    marginTop: 12,
                    fontSize: 12,
                    color: colors.ink00,
                }}
            >
                {/* Header row */}
                <div
                    style={{
                        display: 'flex',
                        background: HEADER_BG,
                        height: 36,
                        alignItems: 'center',
                        borderBottom: `1px solid ${BORDER}`,
                        fontWeight: 600,
                        fontSize: 11,
                        color: colors.ink20,
                        letterSpacing: 0.3,
                    }}
                >
                    <div style={{ width: COL_BUREAU, paddingLeft: 16 }}>Bureau</div>
                    <div style={{ width: COL_SCORE }}>Score</div>
                    <div style={{ width: COL_DPD, textAlign: 'center' }}>DPD Accounts</div>
                    <div style={{ width: COL_OVERDUE, textAlign: 'center' }}>Overdue</div>
                    <div style={{ width: COL_MAXDPD, textAlign: 'center' }}>Max DPD</div>
                    <div style={{ width: COL_FETCHED, textAlign: 'right', paddingRight: 16 }}>Fetched</div>
                </div>

                {d.directors.map((dir, di) => (
                    <div key={di}>
                        <div
                            style={{
                                display: 'flex',
                                background: GROUP_BG,
                                height: GROUP_H + 4,
                                alignItems: 'center',
                                paddingLeft: 16,
                                borderBottom: `1px solid ${BORDER}`,
                                fontWeight: 700,
                                color: colors.ink00,
                            }}
                        >
                            {dir.name}
                        </div>
                        {dir.rows.map((r, ri) => {
                            const isLast = di === d.directors.length - 1 && ri === dir.rows.length - 1;
                            return (
                                <div
                                    key={ri}
                                    style={{
                                        display: 'flex',
                                        height: ROW_H + 6,
                                        alignItems: 'center',
                                        borderBottom: isLast ? 'none' : `1px solid ${BORDER}`,
                                    }}
                                >
                                    <div style={{ width: COL_BUREAU, paddingLeft: 48, color: colors.ink40, fontSize: 12 }}>{r.bureau}</div>
                                    <div
                                        style={{
                                            width: COL_SCORE,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            paddingRight: 16,
                                        }}
                                    >
                                        <span style={{ fontWeight: 500, color: colors.ink00, whiteSpace: 'nowrap' }}>
                                            {fmtScore(r.score)}
                                        </span>
                                        <ScreenScoreBar score={r.score} />
                                    </div>
                                    <div style={{ width: COL_DPD, display: 'flex', justifyContent: 'center' }}>
                                        <ScreenBadge tone={r.dpdAccounts == null ? 'muted' : dpdAccountsTone(r.dpdAccounts)}>
                                            {r.dpdAccounts == null ? '—' : r.dpdAccounts}
                                        </ScreenBadge>
                                    </div>
                                    <div style={{ width: COL_OVERDUE, display: 'flex', justifyContent: 'center' }}>
                                        <ScreenBadge tone={r.currentOverdue == null ? 'muted' : overdueTone(r.currentOverdue)}>
                                            {r.currentOverdue == null ? '—' : fmtRupees(r.currentOverdue)}
                                        </ScreenBadge>
                                    </div>
                                    <div style={{ width: COL_MAXDPD, display: 'flex', justifyContent: 'center' }}>
                                        <ScreenBadge tone={r.maxDpds == null ? 'muted' : maxDpdTone(r.maxDpds)}>
                                            {r.maxDpds == null ? '—' : fmtDays(r.maxDpds)}
                                        </ScreenBadge>
                                    </div>
                                    <div
                                        style={{
                                            width: COL_FETCHED,
                                            paddingRight: 16,
                                            textAlign: 'right',
                                            color: colors.ink40,
                                            fontSize: 11,
                                        }}
                                    >
                                        {fmtFetched(r.fetchedDaysAgo)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </ScreenSection>
    );
};

// Reference table width for layout audits — kept as a runtime constant so a
// linter doesn't strip the COL_* declarations if someone re-uses one.
void TABLE_WIDTH;

export const directorBureauModule: ModuleDefinition = {
    meta: {
        id: 'directorBureau',
        title: 'Director Bureau',
        description: 'Per-bureau credit health (CIBIL / Equifax / Experian) for every director.',
        dataSource: 'polus/creditBureau/directorsOverview',
    },
    hasData: (d) => !!d.directorBureau && d.directorBureau.directors.length > 0,
    Pdf: PdfDirectorBureau,
    Screen: ScreenDirectorBureau,
};
